import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Calendar, User, ArrowRight, ChevronLeft, ChevronRight, Search, X } from 'lucide-react'
import { useBlogs, BlogFilters } from '../hooks/useBlogs'
import { Helmet } from 'react-helmet-async'
import JsonLd from '../components/JsonLd'

const categories = [
  'All',
  'Destinations',
  'Tips',
  'Travel',
  'Adventure',
  'Luxury',
  'Romance',
  'Family',
]

export default function Blog() {
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [searchTerm, setSearchTerm] = useState('')

  // Read initial page from URL
  const queryParams = new URLSearchParams(window.location.search)
  const initialPage = parseInt(queryParams.get('page') || '1', 10)

  // Function to update URL with new page
  const updateUrlPage = (page: number) => {
    window.history.pushState({}, '', `/blog?page=${page}`)
  }

  const { blogs, pagination, loading, error, applyFilters, loadPage } = useBlogs({ initialPage })

  // Override pagination functions to update URL
  const goToNextPageWithUrl = () => {
    if (pagination.has_next) {
      const nextPage = pagination.current_page + 1
      loadPage(nextPage)
      updateUrlPage(nextPage)
    }
  }

  const goToPrevPageWithUrl = () => {
    if (pagination.has_prev) {
      const prevPage = pagination.current_page - 1
      loadPage(prevPage)
      updateUrlPage(prevPage)
    }
  }

  const loadPageWithUrl = (page: number) => {
    loadPage(page)
    updateUrlPage(page)
  }

  const buildFilters = (): BlogFilters => {
    const filters: BlogFilters = {}

    if (selectedCategory !== 'All') {
      filters.category = selectedCategory
    }

    if (searchTerm) {
      filters.search = searchTerm
    }

    return filters
  }

  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category)
    applyFilters(buildFilters())
  }

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    applyFilters(buildFilters())
  }

  const clearAllFilters = () => {
    setSelectedCategory('All')
    setSearchTerm('')
    applyFilters({})
  }

  const hasActiveFilters = selectedCategory !== 'All' || searchTerm

  const getImageUrl = (blog: any) => {
    return (
      blog.featured_image?.url ||
      'https://images.unsplash.com/photo-1488646953014-85cb44e25828?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80'
    )
  }

  if (loading && !blogs.length) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="mx-auto max-w-2xl text-center">
              <div className="h-10 bg-gray-300 rounded w-64 mx-auto mb-6"></div>
              <div className="h-6 bg-gray-300 rounded w-96 mx-auto"></div>
            </div>
            <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col items-start">
                  <div className="aspect-[16/9] w-full rounded-2xl bg-gray-300"></div>
                  <div className="mt-8 space-y-3 w-full">
                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                    <div className="h-6 bg-gray-300 rounded w-full"></div>
                    <div className="h-16 bg-gray-300 rounded w-full"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-indojet-light py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Something went wrong
            </h2>
            <p className="mt-6 text-lg leading-8 text-red-600">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-8 btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-indojet-light py-24 sm:py-32">
      <Helmet>
        <title>Travel News | IndoJet Travel</title>
        <meta
          name="description"
          content="Travel tips, destination guides, and inspiration from IndoJet Travel experts."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/blog' : '/blog'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Travel News | IndoJet Travel" />
        <meta
          property="og:description"
          content="Travel tips, destination guides, and inspiration from IndoJet Travel experts."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/blog'}
        />
        <meta property="og:image" content="/image.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={[
          {
            '@context': 'https://schema.org',
            '@type': 'Blog',
            name: 'IndoJet Travel Blog',
            url: typeof window !== 'undefined' ? window.location.href : '/blog',
            description:
              'Travel tips, destination guides, and inspiration from IndoJet Travel experts.',
          },
          {
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              {
                '@type': 'ListItem',
                position: 1,
                name: 'Home',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/',
              },
              {
                '@type': 'ListItem',
                position: 2,
                name: 'Blog',
                item: (typeof window !== 'undefined' ? window.location.origin : '') + '/blog',
              },
            ],
          },
        ]}
      />
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        {/* Header */}
        <div className="mx-auto max-w-2xl text-center">
          <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Travel News
          </h2>
          <p className="mt-6 text-lg leading-8 text-gray-600">
            Stay updated with the latest travel tips, destination guides, and travel inspiration
            from our experts.
          </p>
        </div>

        {/* Search and Filter Bar */}
        <div className="mx-auto mt-12 max-w-4xl">
          <form onSubmit={handleSearch} className="flex flex-col gap-4">
            <div className="flex gap-2">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search blog posts..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <button type="submit" className="btn-primary">
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Categories Filter */}
        <div className="mx-auto mt-8 flex flex-wrap justify-center gap-4">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => handleCategoryChange(category)}
              className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-[#182c78] text-white'
                  : 'bg-gray-100 text-gray-900 hover:bg-[#182c78] hover:text-white'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="mx-auto mt-6 max-w-4xl">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-gray-600">Active filters:</span>
              {selectedCategory !== 'All' && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#182c78] text-white text-sm">
                  Category: {selectedCategory}
                  <X
                    className="h-3 w-3 cursor-pointer"
                    onClick={() => setSelectedCategory('All')}
                  />
                </span>
              )}
              {searchTerm && (
                <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                  Search: {searchTerm}
                  <X className="h-3 w-3 cursor-pointer" onClick={() => setSearchTerm('')} />
                </span>
              )}
              <button
                onClick={clearAllFilters}
                className="text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Clear all
              </button>
            </div>
          </div>
        )}

        {/* Results Info */}
        <div className="mx-auto mt-8 text-center text-sm text-gray-600">
          Showing {blogs.length} of {pagination.total_count} blog posts
          {selectedCategory !== 'All' && ` in "${selectedCategory}"`}
          {searchTerm && ` matching "${searchTerm}"`}
        </div>

        {/* Blog Posts */}
        <div className="mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
          {blogs.map((post) => {
            const imageUrl = getImageUrl(post)

            return (
              <article key={post.id} className="flex flex-col items-start group">
                <Link to={`/blog/${post.slug}`} className="w-full">
                  <div className="relative w-full">
                    <img
                      src={imageUrl}
                      alt={post.title}
                      className="aspect-[16/9] w-full rounded-2xl bg-gray-100 object-cover sm:aspect-[2/1] lg:aspect-[3/2] group-hover:shadow-lg transition-shadow duration-300"
                    />
                    <div className="absolute inset-0 rounded-2xl ring-1 ring-inset ring-gray-900/10" />
                    <div className="absolute top-4 left-4 bg-[#182c78] text-white px-3 py-1 rounded-full text-sm font-medium">
                      {post.category}
                    </div>
                  </div>
                </Link>
                <div className="max-w-xl w-full">
                  <div className="mt-8 flex items-center gap-x-4 text-xs">
                    <time
                      dateTime={post.created_at}
                      className="text-gray-500 flex items-center gap-1"
                    >
                      <Calendar className="h-3 w-3" />
                      {new Date(post.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </time>
                    <div className="text-gray-500 flex items-center gap-1">
                      <User className="h-3 w-3" />
                      {post.author_name}
                    </div>
                  </div>
                  <div className="group relative">
                    <Link to={`/blog/${post.slug}`}>
                      <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600 transition-colors">
                        {post.title}
                      </h3>
                    </Link>
                    <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                      {post.excerpt}
                    </p>
                  </div>
                  <div className="mt-6">
                    <Link
                      to={`/blog/${post.slug}`}
                      className="inline-flex items-center text-[#182c78] hover:opacity-80 font-medium text-sm"
                    >
                      Read More
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Link>
                  </div>
                </div>
              </article>
            )
          })}
        </div>

        {/* No results */}
        {blogs.length === 0 && !loading && (
          <div className="mx-auto mt-16 max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-gray-900">No blog posts found</h3>
            <p className="mt-2 text-sm text-gray-600">
              Try adjusting your search or filter criteria.
            </p>
            <button onClick={clearAllFilters} className="mt-4 btn-secondary">
              Clear all filters
            </button>
          </div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <div className="mx-auto mt-20 flex items-center justify-center gap-4">
            <button
              onClick={goToPrevPageWithUrl}
              disabled={!pagination.has_prev}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />
              Previous
            </button>

            <div className="flex items-center gap-2">
              {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                let page
                if (pagination.total_pages <= 7) {
                  page = i + 1
                } else {
                  const current = pagination.current_page
                  if (current <= 4) {
                    page = i + 1
                  } else if (current >= pagination.total_pages - 3) {
                    page = pagination.total_pages - 6 + i
                  } else {
                    page = current - 3 + i
                  }
                }

                return (
                  <button
                    key={page}
                    onClick={() => loadPageWithUrl(page)}
                    className={`px-3 py-2 text-sm font-medium rounded-md ${
                      page === pagination.current_page
                        ? 'bg-chelsea-orange text-white'
                        : 'text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {page}
                  </button>
                )
              })}
            </div>

            <button
              onClick={goToNextPageWithUrl}
              disabled={!pagination.has_next}
              className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        )}

        {/* Loading indicator */}
        {loading && blogs.length > 0 && (
          <div className="mx-auto mt-20 text-center">
            <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600">
              <div className="animate-spin h-4 w-4 border-2 border-chelsea-orange border-t-transparent rounded-full mr-2"></div>
              Loading...
            </div>
          </div>
        )}

        {/* CTA Section */}
        <div className="mt-24 rounded-2xl bg-gray-50 px-6 py-16 sm:px-16">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-2xl font-bold tracking-tight text-gray-900">
              Want to contribute to our blog?
            </h2>
            <p className="mt-4 text-lg leading-8 text-gray-600">
              Share your travel experiences and tips with our community of travelers.
            </p>
            <div className="mt-8">
              <a href="/contact" className="btn-primary">
                Get in Touch
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
